/**
 * What `providers.health_state` means, in one place.
 *
 * It was in several, and they disagreed. The backend writes `'HEALTHY'` or
 * `'FAILED'`; the admin Overview compared against `'OK'` in three places, so
 * every provider that was working showed red on the first screen an operator
 * opens, and every one of them was counted under "needs attention". Nothing
 * was wrong with any of them.
 *
 * `'OK'` is still accepted because a row written by an older build may hold
 * it, and treating a healthy provider as broken is the failure this file
 * exists to stop.
 */

export type ProviderHealth = 'healthy' | 'failed' | 'unknown';

const HEALTHY = new Set(['HEALTHY', 'OK']);
const UNKNOWN = new Set(['UNKNOWN', '', 'PENDING']);

export function providerHealth(state: string | null | undefined): ProviderHealth {
  const value = (state ?? '').toUpperCase();
  if (HEALTHY.has(value)) return 'healthy';
  // Anything unrecognised is unknown rather than failed. A state this build
  // has not heard of is not evidence that a provider is broken, and colouring
  // it red is how a working platform looks like a burning one.
  if (UNKNOWN.has(value) || !value) return 'unknown';
  return value === 'FAILED' || value === 'DEGRADED' ? 'failed' : 'unknown';
}

export function isProviderHealthy(state: string | null | undefined): boolean {
  return providerHealth(state) === 'healthy';
}

/** Only a provider that has actually failed a check needs attention. */
export function needsAttention(provider: {
  enabled: boolean;
  health_state: string | null;
}): boolean {
  return provider.enabled && providerHealth(provider.health_state) === 'failed';
}

export const HEALTH_TONE: Record<ProviderHealth, 'green' | 'red' | 'gray'> = {
  healthy: 'green',
  failed: 'red',
  unknown: 'gray',
};

export const HEALTH_LABEL: Record<ProviderHealth, string> = {
  healthy: 'healthy',
  failed: 'failed',
  unknown: 'not checked',
};
